import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Flame,
    Leaf,
    Wind,
    Waves,
    RotateCcw,
    Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { describeCombo, DAILY_RITUALS, DOSHAS } from "@/constants/dosha";
import { TID } from "@/constants/testIds";

const ELEMENT_ICON = {
    vata: Wind,
    pitta: Flame,
    kapha: Waves,
};

function ScoreBar({ dosha, value }) {
    const Icon = ELEMENT_ICON[dosha] || Leaf;
    const info = DOSHAS[dosha];
    return (
        <div>
            <div className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 font-semibold text-veda-onSurface">
                    <Icon size={13} className="text-veda-brand" />
                    {info.name}
                </span>
                <span className="tabular-nums text-veda-muted">{value}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full bg-veda-surface2">
                <div
                    className="h-full bg-veda-brand transition-all"
                    style={{ width: `${value}%` }}
                />
            </div>
        </div>
    );
}

export default function DoshaProfile() {
    const nav = useNavigate();
    const location = useLocation();
    const { user, refresh } = useAuth();
    const passed = location.state?.profile;
    const [profile, setProfile] = useState(passed || null);
    const [loading, setLoading] = useState(!passed);

    useEffect(() => {
        if (profile) return;
        (async () => {
            try {
                const { data } = await api.get("/dosha/profile");
                setProfile(data.profile);
            } catch {
                /* noop */
            } finally {
                setLoading(false);
            }
        })();
    }, [profile]);

    async function retake() {
        if (!window.confirm("Reset your dosha profile and retake the quiz?")) return;
        try {
            await api.delete("/dosha/profile");
            await refresh();
            toast.success("Profile reset — take the quiz again anytime.");
            nav("/wellness/quiz");
        } catch {
            toast.error("Could not reset.");
        }
    }

    if (loading) {
        return (
            <div className="veda-container py-24 text-center text-sm text-veda-muted">
                Loading your dosha…
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="veda-container py-24 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                    No profile yet
                </p>
                <h1 className="font-display mt-2 text-4xl text-veda-onSurface">
                    Discover your dosha first.
                </h1>
                <Link
                    to="/wellness/quiz"
                    className="mt-6 inline-flex items-center gap-1 bg-veda-inverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse"
                >
                    Take the quiz <Sparkles size={13} />
                </Link>
            </div>
        );
    }

    const info = describeCombo(profile.primary);
    const rituals =
        DAILY_RITUALS[(profile.primary || "").split("-")[0]] ||
        DAILY_RITUALS.vata;
    const scores = profile.scores || {};

    return (
        <div data-testid={TID.doshaProfile} className="pb-20">
            <div className="border-b border-veda-border">
                <div className="veda-container-narrow py-10 md:py-16">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={14} /> Home
                    </Link>
                    <div className="mx-auto max-w-2xl">
                        <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                            {user?.name ? `${user.name}'s Prakriti` : "Your Prakriti"}
                        </p>
                        <h1
                            data-testid={TID.doshaProfileName}
                            className="font-display mt-2 text-4xl text-veda-onSurface md:text-6xl"
                        >
                            {info?.name}
                        </h1>
                        <p className="mt-2 text-sm text-veda-muted md:text-base">
                            {info?.element} · {info?.tagline}
                        </p>
                        <p className="mt-6 text-sm leading-relaxed text-veda-onSurface/85 md:text-base">
                            {info?.summary}
                        </p>

                        <div className="mt-8 grid gap-4 sm:grid-cols-2">
                            <div className="border border-veda-border bg-veda-surface p-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                    Common traits
                                </p>
                                <ul className="mt-2 flex flex-wrap gap-1.5">
                                    {info?.traits.map((t) => (
                                        <li
                                            key={t}
                                            className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface"
                                        >
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="border border-veda-border bg-veda-surface p-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                    Your body loves
                                </p>
                                <ul className="mt-2 flex flex-wrap gap-1.5">
                                    {info?.careWords.map((t) => (
                                        <li
                                            key={t}
                                            className="rounded-pill border border-veda-brand/40 bg-veda-brand/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-brand"
                                        >
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="veda-container-narrow py-12 md:py-16">
                <div className="mx-auto max-w-2xl">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        Your dosha balance
                    </p>
                    <h2 className="font-display mt-1 text-2xl text-veda-onSurface md:text-3xl">
                        How your body is composed
                    </h2>
                    <div className="mt-6 flex flex-col gap-4">
                        <ScoreBar dosha="vata" value={scores.vata || 0} />
                        <ScoreBar dosha="pitta" value={scores.pitta || 0} />
                        <ScoreBar dosha="kapha" value={scores.kapha || 0} />
                    </div>
                </div>
            </div>

            <div className="border-t border-veda-border bg-veda-surface2/40">
                <div className="veda-container-narrow py-12 md:py-16">
                    <div className="mx-auto max-w-2xl">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                            Start with your rhythm
                        </p>
                        <h2 className="font-display mt-1 text-2xl text-veda-onSurface md:text-3xl">
                            Three small rituals for your day
                        </h2>
                        <p className="mt-2 text-sm text-veda-muted">
                            Ayurveda works through gentle repetition. Try one
                            of these for seven mornings — see how your body
                            responds.
                        </p>
                        <ul className="mt-6 divide-y divide-veda-divider border border-veda-border bg-veda-surface">
                            {rituals.map((r) => (
                                <li
                                    key={r.title}
                                    className="flex items-start gap-4 p-4 md:p-5"
                                >
                                    <span className="min-w-[64px] text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand">
                                        {r.time}
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold text-veda-onSurface md:text-base">
                                            {r.title}
                                        </p>
                                        <p className="mt-1 text-xs leading-relaxed text-veda-muted md:text-sm">
                                            {r.body}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link
                                to="/"
                                className="inline-flex items-center gap-1 bg-veda-inverse px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                            >
                                Back to home
                            </Link>
                            <button
                                onClick={retake}
                                data-testid={TID.doshaProfileRetake}
                                className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                            >
                                <RotateCcw size={12} /> Retake quiz
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
